import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  Smartphone,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import { COUNTRIES, PhoneInput, type Country } from "./PhoneInput";
import { OtpInput } from "./OtpInput";
import {
  useResendCode,
  useStartSession,
  useSubmitCode,
  useSubmitPassword,
  type Account,
} from "@/api/accounts";
import {
  emptyProxyForm,
  isProxyFormValid,
  ProxySettingsForm,
  proxyInputFromForm,
  type ProxyFormState,
} from "./ProxySettingsForm";

type Step = "phone" | "code" | "password" | "success";

interface AddAccountSheetProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (account: Account) => void;
}

export function AddAccountSheet({ open, onClose, onSuccess }: AddAccountSheetProps) {
  const [step, setStep] = useState<Step>("phone");
  const [country, setCountry] = useState<Country>(COUNTRIES[0]);
  const [phone, setPhone] = useState("");
  const [sessionId, setSessionId] = useState<string>("");
  const [codeLength, setCodeLength] = useState(5);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [resendIn, setResendIn] = useState(60);
  const [createdAccount, setCreatedAccount] = useState<Account | null>(null);
  const [proxy, setProxy] = useState<ProxyFormState>(() => emptyProxyForm());

  const startSession = useStartSession();
  const submitCode = useSubmitCode(sessionId);
  const submitPassword = useSubmitPassword(sessionId);
  const resendCode = useResendCode(sessionId);

  // Reset when re-opened
  useEffect(() => {
    if (open) {
      setStep("phone");
      setPhone("");
      setSessionId("");
      setCode("");
      setPassword("");
      setCodeError(false);
      setPasswordError(false);
      setCreatedAccount(null);
      setProxy(emptyProxyForm());
    }
  }, [open]);

  // Resend countdown
  useEffect(() => {
    if (step !== "code") return;
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [step, resendIn]);

  // Auto-dismiss success
  useEffect(() => {
    if (step !== "success" || !createdAccount) return;
    const t = setTimeout(() => {
      onSuccess(createdAccount);
      onClose();
    }, 1600);
    return () => clearTimeout(t);
  }, [step, createdAccount, onSuccess, onClose]);

  const fullPhone = `${country.dial}${phone}`;
  const phoneIsValid = phone.length >= 7;
  const proxyIsValid = isProxyFormValid(proxy);

  const handleSendCode = async () => {
    if (!phoneIsValid || !proxyIsValid) return;
    haptic.impact("medium");
    try {
      const resp = await startSession.mutateAsync({
        phone: fullPhone,
        proxy: proxy.enabled ? proxyInputFromForm(proxy) : null,
      });
      setSessionId(resp.session_id);
      setCodeLength(resp.code_length);
      setResendIn(resp.resend_in_seconds);
      setStep("code");
    } catch (err) {
      haptic.notify("error");
      toast.error((err as Error)?.message || "Не удалось отправить код");
    }
  };

  const handleSubmitCode = async (value: string) => {
    if (value.length !== codeLength) return;
    setCodeError(false);
    try {
      const resp = await submitCode.mutateAsync(value);
      if (resp.kind === "invalid_code") {
        setCodeError(true);
        haptic.notify("error");
        setTimeout(() => setCodeError(false), 1500);
      } else if (resp.kind === "needs_password") {
        haptic.notify("warning");
        setStep("password");
      } else {
        haptic.notify("success");
        setCreatedAccount(resp.account);
        setStep("success");
      }
    } catch {
      setCodeError(true);
      haptic.notify("error");
    }
  };

  const handleSubmitPassword = async () => {
    if (!password) return;
    setPasswordError(false);
    haptic.impact("medium");
    try {
      const resp = await submitPassword.mutateAsync(password);
      if (resp.kind === "invalid_password") {
        setPasswordError(true);
        haptic.notify("error");
        setTimeout(() => setPasswordError(false), 1500);
      } else {
        haptic.notify("success");
        setCreatedAccount(resp.account);
        setStep("success");
      }
    } catch {
      setPasswordError(true);
      haptic.notify("error");
    }
  };

  const handleResend = async () => {
    if (resendIn > 0) return;
    haptic.impact("light");
    try {
      const resp = await resendCode.mutateAsync();
      setResendIn(resp.resend_in_seconds);
      setCode("");
      setCodeError(false);
    } catch {
      haptic.notify("error");
    }
  };

  const goBack = () => {
    haptic.select();
    if (step === "code") setStep("phone");
    else if (step === "password") setStep("code");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            className={cn(
              "fixed inset-x-0 bottom-0 top-12 z-50",
              "glass-strong rounded-t-[28px] overflow-hidden",
              "border-t border-white/[0.08] flex flex-col",
            )}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
          >
            {/* Top sun glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[120%] aspect-square rounded-full"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(255,107,53,0.45) 0%, rgba(255,69,0,0.18) 35%, rgba(0,0,0,0) 65%)",
                filter: "blur(40px)",
              }}
            />

            {/* Drag handle */}
            <div className="relative flex items-center justify-center pt-3 pb-1 shrink-0">
              <div className="w-10 h-1 rounded-full bg-white/15" />
            </div>

            {/* Header with back/close */}
            <div className="relative flex items-center justify-between px-5 pt-2 pb-1 shrink-0">
              {step === "phone" || step === "success" ? (
                <div className="size-9" />
              ) : (
                <button
                  onClick={goBack}
                  className="size-9 grid place-items-center rounded-full bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                  aria-label="Назад"
                >
                  <ArrowLeft className="size-4" />
                </button>
              )}

              <StepIndicator step={step} />

              <button
                onClick={onClose}
                className="size-9 grid place-items-center rounded-full bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                aria-label="Закрыть"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Step body */}
            <div className="relative flex-1 overflow-y-auto px-6 pb-8">
              <AnimatePresence mode="wait">
                {step === "phone" && (
                  <StepPhone
                    key="phone"
                    country={country}
                    onCountryChange={setCountry}
                    phone={phone}
                    onPhoneChange={setPhone}
                    onSubmit={handleSendCode}
                    isLoading={startSession.isPending}
                    isValid={phoneIsValid}
                    proxy={proxy}
                    onProxyChange={setProxy}
                    isProxyValid={proxyIsValid}
                  />
                )}
                {step === "code" && (
                  <StepCode
                    key="code"
                    fullPhone={fullPhone}
                    codeLength={codeLength}
                    code={code}
                    onCodeChange={setCode}
                    onComplete={handleSubmitCode}
                    error={codeError}
                    isLoading={submitCode.isPending}
                    resendIn={resendIn}
                    onResend={handleResend}
                    onEditPhone={() => setStep("phone")}
                  />
                )}
                {step === "password" && (
                  <StepPassword
                    key="password"
                    password={password}
                    onPasswordChange={setPassword}
                    showPassword={showPassword}
                    onToggleShow={() => setShowPassword((s) => !s)}
                    onSubmit={handleSubmitPassword}
                    error={passwordError}
                    isLoading={submitPassword.isPending}
                  />
                )}
                {step === "success" && createdAccount && (
                  <StepSuccess key="success" account={createdAccount} />
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ============================================================
// Step indicator
// ============================================================

function StepIndicator({ step }: { step: Step }) {
  const idx = step === "phone" ? 0 : step === "code" ? 1 : 2;
  return (
    <div className="flex items-center gap-1.5">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          animate={{
            width: i === idx ? 22 : 6,
            opacity: i <= idx ? 1 : 0.3,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className={cn(
            "h-1.5 rounded-full",
            i <= idx ? "bg-accent" : "bg-white/15",
          )}
        />
      ))}
    </div>
  );
}

// ============================================================
// Step 1 — Phone
// ============================================================

function StepPhone({
  country,
  onCountryChange,
  phone,
  onPhoneChange,
  onSubmit,
  isLoading,
  isValid,
  proxy,
  onProxyChange,
  isProxyValid,
}: {
  country: Country;
  onCountryChange: (c: Country) => void;
  phone: string;
  onPhoneChange: (p: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  isValid: boolean;
  proxy: ProxyFormState;
  onProxyChange: (value: ProxyFormState) => void;
  isProxyValid: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6 pt-4"
    >
      <div className="text-center space-y-3">
        <div className="mx-auto size-14 rounded-2xl bg-accent-soft text-accent grid place-items-center">
          <Smartphone className="size-6" />
        </div>
        <div>
          <div className="text-[24px] font-semibold tracking-tight">Подключить аккаунт</div>
          <div className="text-sm text-text-muted mt-1.5 max-w-[280px] mx-auto leading-relaxed">
            Введите номер, привязанный к&nbsp;Telegram. Мы отправим код подтверждения в&nbsp;приложение.
          </div>
        </div>
      </div>

      <PhoneInput
        country={country}
        onCountryChange={onCountryChange}
        phone={phone}
        onPhoneChange={onPhoneChange}
        autoFocus
      />

      <ProxySettingsForm
        value={proxy}
        onChange={onProxyChange}
        disabled={isLoading}
      />

      <Button
        size="lg"
        fullWidth
        rightIcon={<ArrowRight />}
        onClick={onSubmit}
        disabled={!isValid || !isProxyValid || isLoading}
        loading={isLoading}
      >
        Получить код
      </Button>

      <div className="text-[11px] text-text-dim text-center px-2 leading-relaxed space-y-1.5">
        <div>
          Сессия хранится зашифрованно. Мы не имеем доступа к&nbsp;вашим личным сообщениям.
        </div>
        <div className="text-text-faint">
          Срок жизни аккаунта в&nbsp;Telegram зависит от истории его IP, даты
          регистрации и политики Telegram — сервис не несёт ответственности
          за блокировки. Подробнее в&nbsp;«Условиях использования».
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================
// Step 2 — Code
// ============================================================

function StepCode({
  fullPhone,
  codeLength,
  code,
  onCodeChange,
  onComplete,
  error,
  isLoading,
  resendIn,
  onResend,
  onEditPhone,
}: {
  fullPhone: string;
  codeLength: number;
  code: string;
  onCodeChange: (c: string) => void;
  onComplete: (c: string) => void;
  error: boolean;
  isLoading: boolean;
  resendIn: number;
  onResend: () => void;
  onEditPhone: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6 pt-4"
    >
      <div className="text-center space-y-3">
        <div>
          <div className="text-[24px] font-semibold tracking-tight">Введите код</div>
          <div className="text-sm text-text-muted mt-1.5 leading-relaxed">
            Мы отправили код в&nbsp;Telegram на номер
            <br />
            <button
              onClick={onEditPhone}
              className="text-white font-medium tabular-nums underline-offset-4 hover:underline"
            >
              {fullPhone}
            </button>
          </div>
        </div>
      </div>

      <motion.div
        animate={error ? { x: [-8, 8, -6, 6, -2, 2, 0] } : { x: 0 }}
        transition={{ duration: 0.4 }}
      >
        <OtpInput
          length={codeLength}
          value={code}
          onChange={onCodeChange}
          onComplete={onComplete}
          error={error}
          disabled={isLoading}
        />
      </motion.div>

      <div className="text-center">
        {resendIn > 0 ? (
          <div className="text-xs text-text-dim">
            Отправить повторно через <span className="tabular-nums text-text-muted">{resendIn}с</span>
          </div>
        ) : (
          <button
            onClick={onResend}
            className="text-xs text-accent hover:text-accent-hot font-medium"
          >
            Отправить код ещё раз
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ============================================================
// Step 3 — 2FA Password
// ============================================================

function StepPassword({
  password,
  onPasswordChange,
  showPassword,
  onToggleShow,
  onSubmit,
  error,
  isLoading,
}: {
  password: string;
  onPasswordChange: (p: string) => void;
  showPassword: boolean;
  onToggleShow: () => void;
  onSubmit: () => void;
  error: boolean;
  isLoading: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6 pt-4"
    >
      <div className="text-center space-y-3">
        <div className="mx-auto size-14 rounded-2xl bg-accent-soft text-accent grid place-items-center">
          <Lock className="size-6" />
        </div>
        <div>
          <div className="text-[24px] font-semibold tracking-tight">Двухфакторная защита</div>
          <div className="text-sm text-text-muted mt-1.5 max-w-[280px] mx-auto leading-relaxed">
            Этот аккаунт защищён облачным паролем Telegram. Введите его, чтобы продолжить.
          </div>
        </div>
      </div>

      <motion.div
        animate={error ? { x: [-8, 8, -6, 6, -2, 2, 0] } : { x: 0 }}
        transition={{ duration: 0.4 }}
        className={cn(
          "relative flex items-stretch h-[58px] rounded-2xl bg-surface border transition-colors",
          "focus-within:border-accent focus-within:shadow-[0_0_0_4px_rgba(255,107,53,0.12)]",
          error ? "border-danger" : "border-white/[0.08]",
        )}
      >
        <input
          autoFocus
          type={showPassword ? "text" : "password"}
          placeholder="Облачный пароль"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
          disabled={isLoading}
          className="flex-1 bg-transparent px-4 text-base outline-none placeholder:text-text-faint"
        />
        <button
          onClick={onToggleShow}
          className="px-4 text-text-dim hover:text-white transition-colors"
          aria-label={showPassword ? "Скрыть" : "Показать"}
          type="button"
        >
          {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </motion.div>

      <Button
        size="lg"
        fullWidth
        onClick={onSubmit}
        disabled={!password || isLoading}
        loading={isLoading}
      >
        Войти
      </Button>

      <div className="text-[11px] text-text-dim text-center px-2 leading-relaxed">
        Это&nbsp;— не пароль Telegram, а&nbsp;дополнительный «облачный» пароль, который вы
        настроили в&nbsp;«Конфиденциальность и&nbsp;безопасность».
      </div>
    </motion.div>
  );
}

// ============================================================
// Step 4 — Success
// ============================================================

function StepSuccess({ account }: { account: Account }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col items-center justify-center pt-12 pb-6 space-y-5 text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 22, delay: 0.05 }}
        className="size-20 rounded-full bg-gradient-accent grid place-items-center shadow-[0_20px_60px_-12px_rgba(255,69,0,0.7)]"
      >
        <CheckCircle2 className="size-10 text-white" strokeWidth={2.5} />
      </motion.div>

      <div>
        <div className="text-[24px] font-semibold tracking-tight">Аккаунт подключён</div>
        <div className="text-sm text-text-muted mt-1.5">
          <span className="text-white font-medium">{account.name}</span>
          <br />
          <span className="tabular-nums">{account.phone}</span>
        </div>
      </div>
    </motion.div>
  );
}
